import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch, API_URL } from '../api';
import { useWS } from '../communication/WSProvider';
import { useAuth } from '../auth/AuthProvider';
import { millisekunderSedan, klockslag } from '../utils/tidsstampel';

/**
 * ProjectChatWidget – sidebar chat widget shown when inside a project.
 * Design per kanplan-project-tabs-v3.html spec (Projektchatt section).
 *
 * Props:
 *   projectId  – numeric project ID
 *   contrastMenu – bool, high-contrast sidebar mode
 *   compact    – bool, sidebar is in compact/collapsed mode
 */
export default function ProjectChatWidget({ projectId, contrastMenu = false, compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { lastEvent } = useWS() || {};
  const { user } = useAuth();
  const myUserId = user?.id;
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('kp-chat-widget-collapsed') === '1'; } catch { return false; }
  });
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [peers, setPeers] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const fetchedRef = useRef(false);
  const inputRef = useRef(null);

  // Persist collapse state
  const toggleCollapse = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('kp-chat-widget-collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  }, []);

  // Fetch peers for name resolution
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/users/peers`);
        if (res.ok) {
          const data = await res.json();
          if (alive) setPeers(Array.isArray(data) ? data : []);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, []);

  // Fetch recent messages for this project
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    fetchedRef.current = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/communication/messages/project/${projectId}?limit=20`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (alive) {
          const items = (json.items || []).slice(-3).reverse(); // last 3, newest first
          setMessages(items);
          fetchedRef.current = true;
        }
      } catch {
        // silent – widget is non-critical
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  // Fetch unread count from project-summaries endpoint
  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch(`${API_URL}/communication/projects/summary?project_ids=${projectId}&limit=1`);
        if (!res.ok) return;
        const json = await res.json();
        const items = json.items || [];
        const match = items.find(it => Number(it?.project?.id) === Number(projectId));
        if (alive && match) {
          setUnreadCount(match.unread || 0);
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [projectId]);

  // Real-time update via WS
  useEffect(() => {
    if (!lastEvent || !fetchedRef.current) return;
    if (lastEvent.type === 'project.message.created' && Number(lastEvent.project_id) === Number(projectId)) {
      const msg = {
        id: lastEvent.id,
        body: lastEvent.body,
        created_at: lastEvent.created_at,
        sender_user_id: lastEvent.sender_user_id,
        sender_name: lastEvent.sender_name || null,
      };
      setMessages(prev => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [msg, ...prev].slice(0, 3);
      });
      // Bump unread unless user is on the communication tab or sender is self
      const isOnComms = location.hash === '#kommunikation';
      const isSelf = myUserId && Number(lastEvent.sender_user_id) === Number(myUserId);
      if (!isOnComms && !isSelf) {
        setUnreadCount(c => c + 1);
      }
    }
  }, [lastEvent, projectId, location.hash, myUserId]);

  // Navigate to communication tab
  const goToChat = useCallback(() => {
    window.location.hash = 'kommunikation';
    // Dispatch event for ProjectDetails to pick up
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }, []);

  const goToMessage = useCallback((/* msgId */) => {
    goToChat();
  }, [goToChat]);

  // Send a quick message directly from the widget
  const sendMessage = useCallback(async () => {
    const body = text.trim();
    if (!body || !projectId || sending) return;
    setSending(true);
    try {
      const res = await apiFetch(`${API_URL}/communication/messages/project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: Number(projectId), body }),
      });
      if (res.ok) {
        setText('');
        // Refresh messages
        const listRes = await apiFetch(`${API_URL}/communication/messages/project/${projectId}?limit=20`);
        if (listRes.ok) {
          const json = await listRes.json();
          setMessages((json.items || []).slice(-3).reverse());
        }
      }
    } catch {}
    setSending(false);
  }, [text, projectId, sending]);

  // Format time
  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const diffMs = millisekunderSedan(iso);
      if (diffMs === null) return '';
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 1) return `${diffDays}d sedan`;
      if (diffDays === 1) return 'igår';
      return klockslag(iso);
    } catch {
      return '';
    }
  };

  // Get display name from peers lookup
  const nameOf = (msg) => {
    if (msg.sender_name || msg.user?.name) return msg.sender_name || msg.user.name;
    const uid = msg.sender_user_id;
    // /users/peers utesluter en själv, så egna meddelanden blev "Användare 1" (F6)
    if (uid && uid === myUserId) return user?.name || user?.email || 'Du';
    if (uid) {
      const peer = peers.find(p => p.id === uid);
      if (peer) return peer.name || peer.email || `Användare ${uid}`;
    }
    return `Användare ${uid || '?'}`;
  };

  // Get initials from name or user_id
  const getInitials = (msg) => {
    const name = nameOf(msg);
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return name.slice(0, 2).toUpperCase();
    }
    return '??';
  };

  // Deterministic avatar color from user id
  const avatarColors = ['#3B82F6', '#059669', '#F59E0B', '#EF4444', '#7C3AED', '#0D9488'];
  const getAvatarColor = (msg) => {
    const uid = msg.sender_user_id || 0;
    return avatarColors[uid % avatarColors.length];
  };

  if (compact) return null; // Don't show in compact sidebar

  const headerBg = contrastMenu
    ? 'rgba(255,255,255,0.04)'
    : 'transparent';
  const headerHover = contrastMenu
    ? 'rgba(255,255,255,0.08)'
    : 'var(--kp-blue-50, #EFF6FF)';
  const borderColor = contrastMenu
    ? 'rgba(255,255,255,0.10)'
    : 'var(--kp-border, #E2E5EA)';
  const textPrimary = contrastMenu ? '#fff' : 'var(--kp-text, #1E293B)';
  const textMuted = contrastMenu ? 'rgba(255,255,255,0.55)' : 'var(--kp-text-muted, #64748B)';
  const textFaint = contrastMenu ? 'rgba(255,255,255,0.4)' : 'var(--kp-text-faint, #94A3B8)';
  const surfaceBg = contrastMenu ? 'rgba(255,255,255,0.03)' : 'var(--kp-white, #fff)';
  const hoverBg = contrastMenu ? 'rgba(255,255,255,0.06)' : 'var(--kp-inset, #F8F9FB)';
  const unreadBg = contrastMenu ? 'rgba(59,130,246,0.12)' : 'var(--kp-blue-50, #EFF6FF)';

  return (
    <>
      <style>{`
        .pcw-header:hover { background: ${headerHover} !important; }
        .pcw-msg:hover { background: ${hoverBg} !important; }
        .pcw-msg.pcw-unread { background: ${unreadBg}; }
        .pcw-msg.pcw-unread:hover { background: ${contrastMenu ? 'rgba(59,130,246,0.18)' : '#DBEAFE'} !important; }
        .pcw-body { max-height: 280px; overflow: hidden; transition: max-height 0.25s ease, opacity 0.2s ease; opacity: 1; }
        .pcw-body.pcw-collapsed { max-height: 0; opacity: 0; overflow: hidden; }
        .pcw-toggle-chev { transition: transform 0.2s ease; }
        .pcw-collapsed-chev { transform: rotate(180deg); }

        /* Dark mode overrides (non-contrast) */
        .dark .pcw-header:not(.pcw-contrast):hover { background: rgba(255,255,255,0.05) !important; }
        .dark .pcw-msg:not(.pcw-contrast):hover { background: rgba(255,255,255,0.04) !important; }
        .dark .pcw-msg.pcw-unread:not(.pcw-contrast) { background: rgba(59,130,246,0.08); }
        .dark .pcw-msg.pcw-unread:not(.pcw-contrast):hover { background: rgba(59,130,246,0.14) !important; }
        @keyframes pcw-spin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ borderTop: `1px solid ${borderColor}` }}>
        {/* Header */}
        <button
          type="button"
          className={`pcw-header ${contrastMenu ? 'pcw-contrast' : ''}`}
          onClick={toggleCollapse}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: headerBg,
            border: 'none',
            cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          aria-expanded={!collapsed}
          aria-label="Projektchatt"
        >
          {/* Chat icon */}
          <span style={{
            width: 30, height: 30, borderRadius: 8,
            background: contrastMenu ? 'rgba(59,130,246,0.2)' : 'var(--kp-accent-light, #EFF6FF)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={contrastMenu ? '#93C5FD' : 'var(--kp-accent, #3B82F6)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          {/* Title */}
          <span style={{
            fontSize: 13, fontWeight: 600,
            color: textPrimary,
            flex: 1, textAlign: 'left',
          }}>
            Projektchatt
          </span>
          {/* Unread badge */}
          {unreadCount > 0 && (
            <span style={{
              background: 'var(--kp-red-fill, #DC2626)', color: '#fff',
              fontSize: 10, fontWeight: 700,
              minWidth: 18, height: 18, borderRadius: 9,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 5px', lineHeight: 1,
            }}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
          {/* Chevron */}
          <svg
            className={`pcw-toggle-chev ${collapsed ? 'pcw-collapsed-chev' : ''}`}
            width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke={textFaint} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>

        {/* Body */}
        <div className={`pcw-body ${collapsed ? 'pcw-collapsed' : ''}`}>
          {/* Messages */}
          <div style={{ padding: '2px 0' }}>
            {loading && (
              <div style={{ padding: '12px 14px' }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ display:'flex', gap:10, padding:'8px 0', opacity: 0.5 }}>
                    <div style={{ width:34, height:34, borderRadius:'50%', background: contrastMenu ? 'rgba(255,255,255,0.08)' : '#E2E5EA' }} />
                    <div style={{ flex:1, display:'flex', flexDirection:'column', gap:4 }}>
                      <div style={{ width:'60%', height:11, borderRadius:4, background: contrastMenu ? 'rgba(255,255,255,0.08)' : '#E2E5EA' }} />
                      <div style={{ width:'80%', height:10, borderRadius:4, background: contrastMenu ? 'rgba(255,255,255,0.06)' : '#ECEEF2' }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loading && messages.length === 0 && (
              <div style={{ padding: '16px 14px', fontSize: 12, color: textFaint, textAlign: 'center' }}>
                Inga meddelanden
              </div>
            )}
            {!loading && messages.map((msg, idx) => {
              const isUnread = idx < unreadCount;
              const isAI = msg.message_type === 'AI_RESPONSE' || msg.sender_user_id === 0;
              return (
                <button
                  key={msg.id || idx}
                  type="button"
                  className={`pcw-msg ${isUnread ? 'pcw-unread' : ''} ${contrastMenu ? 'pcw-contrast' : ''}`}
                  onClick={() => goToMessage(msg.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '9px 14px',
                    background: isAI ? (contrastMenu ? 'rgba(124,58,237,0.08)' : 'rgba(124,58,237,0.04)') : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s',
                    position: 'relative',
                    borderLeft: isAI ? '3px solid #7C3AED' : 'none',
                  }}
                  title={isAI ? 'KanAI-svar' : 'Gå till meddelande'}
                >
                  {/* Unread dot */}
                  {isUnread && !isAI && (
                    <span style={{
                      position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)',
                      width: 6, height: 6, borderRadius: '50%', background: '#3B82F6',
                    }} />
                  )}
                  {/* Avatar */}
                  {isAI ? (
                    <span style={{
                      width: 34, height: 34, borderRadius: '50%',
                      background: '#7C3AED', color: '#fff',
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      AI
                    </span>
                  ) : (
                  <span style={{
                    width: 34, height: 34, borderRadius: '50%',
                    background: getAvatarColor(msg), color: '#fff',
                    fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {getInitials(msg)}
                  </span>
                  )}
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{
                        fontSize: 11.5, fontWeight: 600,
                        color: isAI ? '#7C3AED' : (isUnread ? 'var(--kp-accent, #3B82F6)' : textPrimary),
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {isAI ? 'KanAI' : nameOf(msg)}
                      </span>
                      <span style={{
                        fontSize: 11, color: textFaint, marginLeft: 8, flexShrink: 0,
                      }}>
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: isUnread ? (contrastMenu ? 'rgba(255,255,255,0.8)' : 'var(--kp-text, #334155)') : textMuted,
                      fontWeight: isUnread ? 500 : 400,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      marginTop: 1,
                    }}>
                      {msg.body || (msg.attachment_name ? `Bifogad fil: ${msg.attachment_name}` : '')}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Visa alla link */}
          {!loading && messages.length > 0 && (
            <div style={{ padding: '0 14px 4px', textAlign: 'right' }}>
              <button
                type="button"
                onClick={goToChat}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 11.5, fontWeight: 500, color: 'var(--kp-accent, #3B82F6)',
                  padding: '4px 0', fontFamily: 'inherit',
                  transition: 'opacity 0.15s',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Visa alla i projektchatt
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          )}

          {/* Input row */}
          <div style={{
            padding: '8px 12px 10px',
            borderTop: `1px solid ${borderColor}`,
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                ref={inputRef}
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Skriv meddelande... (@kanai för AI)"
                style={{
                  flex: 1,
                  height: 32,
                  border: `1px solid ${contrastMenu ? 'rgba(255,255,255,0.12)' : 'var(--kp-border, #E2E5EA)'}`,
                  borderRadius: 8,
                  padding: '0 10px',
                  fontSize: 12,
                  background: contrastMenu ? 'rgba(255,255,255,0.04)' : 'var(--kp-inset, #F8F9FB)',
                  color: contrastMenu ? '#fff' : 'var(--kp-text, #1E293B)',
                  outline: 'none',
                  fontFamily: 'inherit',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--kp-accent, #3B82F6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = contrastMenu ? 'rgba(255,255,255,0.12)' : 'var(--kp-border, #E2E5EA)'; e.currentTarget.style.boxShadow = 'none'; }}
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!text.trim() || sending}
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: text.trim() ? 'var(--kp-accent, #3B82F6)' : (contrastMenu ? 'rgba(255,255,255,0.08)' : 'var(--kp-inset, #F8F9FB)'),
                  border: 'none', cursor: text.trim() ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.15s',
                  flexShrink: 0,
                  opacity: sending ? 0.6 : 1,
                }}
                title="Skicka meddelande"
                aria-label="Skicka meddelande"
                onMouseEnter={e => { if (text.trim()) e.currentTarget.style.background = '#2563EB'; }}
                onMouseLeave={e => { if (text.trim()) e.currentTarget.style.background = 'var(--kp-accent, #3B82F6)'; }}
              >
                {sending ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'pcw-spin 0.7s linear infinite' }}>
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={text.trim() ? '#fff' : textFaint} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
